import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { fromEmpty } from '@infl-ts/util-string-from-empty';
import { addValidProtocol, extension } from '@infl-ts/util-url';
import { formatISO, isValid, parseISO } from 'date-fns';
import { extensionToMime } from 'filestack-js';
import * as A from 'fp-ts/Array';
import { absurd, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as R from 'fp-ts/Record';
import * as S from 'fp-ts/Semigroup';
import * as Str from 'fp-ts/string';
import { UploadedFile } from '../../file-uploader';
import { DateResponse, ResponseType, StringArrayResponse, StringResponse } from './codecs';

var getDefaultQuestionValue = function getDefaultQuestionValue(question) {
  var _question$currentValu, _question$currentValu2, _question$currentValu3;

  if (question.__typename === 'CheckboxQuestion') return (_question$currentValu = question.currentValues) !== null && _question$currentValu !== void 0 ? _question$currentValu : [];

  if (question.__typename === 'DateQuestion') {
    return pipe(question.currentValue, O.fromNullable, O.map(parseISO), O.chain(O.fromPredicate(isValid)), O.map(formatISO), O.toNullable);
  }

  if (question.__typename === 'DropdownQuestion') {
    return question.currentValue && question.options.includes({
      description: question.currentValue
    }) ? question.currentValue : null;
  }

  if (question.__typename === 'RadioButtonQuestion') {
    return question.currentValue && question.options.includes({
      description: question.currentValue
    }) ? question.currentValue : '';
  }

  if (question.__typename === 'LinkQuestion' || question.__typename === 'LongAnswerQuestion' || question.__typename === 'ShortAnswerQuestion') return (_question$currentValu2 = question.currentValue) !== null && _question$currentValu2 !== void 0 ? _question$currentValu2 : '';
  if (question.__typename === 'RatingQuestion') return (_question$currentValu3 = question.currentValue) !== null && _question$currentValu3 !== void 0 ? _question$currentValu3 : '0';

  if (question.__typename === 'ImageUploadQuestion' || question.__typename === 'FileUploadQuestion' || question.__typename === 'VideoUploadQuestion') {
    return pipe(O.Do, O.apS('url', pipe(question.currentValue, O.fromNullable, O.chain(fromEmpty))), O["let"]('filename', function () {
      return 'Default';
    }), O["let"]('mimetype', function (_ref) {
      var url = _ref.url;
      return pipe(extension(url), O.getOrElseW(function () {
        return question.__typename === 'ImageUploadQuestion' ? '.jpeg' : question.__typename === 'FileUploadQuestion' ? '.pdf' : '.mp4';
      }), extensionToMime);
    }), O.apS('handle', O.some('')), O.toNullable);
  }

  return absurd(question);
};

export var formatQuestionDefaults = function formatQuestionDefaults(questions) {
  return R.fromFoldableMap(S.last(), A.Foldable)(questions, function (q) {
    return [q.id, getDefaultQuestionValue(q)];
  });
};

var formatQuestion = function formatQuestion(_ref2) {
  var id = _ref2.id,
      response = _ref2.response,
      __typename = _ref2.question.__typename;

  if (StringArrayResponse.is(response)) {
    if (A.isEmpty(response)) return O.none;
    return __typename === 'CheckboxQuestion' ? O.some({
      id: id,
      checkboxResponse: response
    }) : O.none;
  }

  if (DateResponse.is(response)) {
    if (__typename === 'DateQuestion') {
      return O.some({
        id: id,
        dateResponse: pipe(response, O.fromNullable, O.chain(function (v) {
          return O.tryCatch(function () {
            return formatISO(v);
          });
        }), O.getOrElse(function () {
          return response.toString();
        }))
      });
    }

    return O.none;
  }

  if (StringResponse.is(response)) {
    if (Str.isEmpty(response)) return O.none;
    if (__typename === 'DateQuestion') return O.some({
      id: id,
      dateResponse: response
    });
    if (__typename === 'DropdownQuestion') return O.some({
      id: id,
      dropDownResponse: response
    });
    if (__typename === 'LinkQuestion') return O.some({
      id: id,
      linkResponse: addValidProtocol(response)
    });
    if (__typename === 'LongAnswerQuestion') return O.some({
      id: id,
      longAnswerResponse: response
    });
    if (__typename === 'ShortAnswerQuestion') return O.some({
      id: id,
      shortAnswerResponse: response
    });
    if (__typename === 'RadioButtonQuestion') return O.some({
      id: id,
      radioResponse: response
    });

    if (__typename === 'RatingQuestion') {
      return pipe(response, Number.parseInt, function (rating) {
        return Number.isNaN(rating) ? O.none : O.some({
          id: id,
          ratingResponse: rating
        });
      });
    }

    return O.none;
  }

  if (UploadedFile.is(response)) {
    if (__typename === 'ImageUploadQuestion') {
      return O.some({
        id: id,
        imageUploadResponse: {
          url: response.url,
          filestack: {
            handle: response.handle
          }
        }
      });
    }

    if (__typename === 'FileUploadQuestion') {
      return O.some({
        id: id,
        fileUploadResponse: {
          url: response.url,
          filestack: {
            handle: response.handle
          }
        }
      });
    }

    if (__typename === 'VideoUploadQuestion') {
      return O.some({
        id: id,
        videoUploadResponse: {
          url: response.url,
          filestack: {
            handle: response.handle
          }
        }
      });
    }

    return O.none;
  }

  if (response === null) return O.none;
  return absurd(response);
};

var formatQuestions = function formatQuestions(questionMap) {
  return A.filterMap(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        id = _ref4[0],
        response = _ref4[1];

    return formatQuestion({
      id: id,
      response: response,
      question: questionMap[id][0]
    });
  });
};

var isQuestionFormField = function isQuestionFormField(questionMap) {
  return function (formField) {
    return ResponseType.is(formField[1]) && !!questionMap[formField[0]];
  };
};

export var extractAndFormatQuestions = function extractAndFormatQuestions(data, questionMap) {
  return pipe(data, A.filter(isQuestionFormField(questionMap)), formatQuestions(questionMap));
};